
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Eye, PencilIcon, Trash2, MoreHorizontal, CheckCircle, XCircle, Clock } from "lucide-react";
import { ListingStatus } from "@/lib/types/adminTypes";

interface ListingActionsDropdownProps {
  listingId: string;
  status: ListingStatus;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (id: string, status: ListingStatus) => void;
  onOpenChange?: (open: boolean) => void;
}

export function ListingActionsDropdown({
  listingId,
  status,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onOpenChange,
}: ListingActionsDropdownProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    onOpenChange?.(isOpen);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="h-8 w-8 p-0" 
          aria-label="Open menu"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onView}>
          <Eye className="mr-2 h-4 w-4" />
          <span>View</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <PencilIcon className="mr-2 h-4 w-4" />
          <span>Edit</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Status change options based on current status */}
        {status !== "draft" && (
          <DropdownMenuItem onClick={() => onStatusChange(listingId, "draft")}>
            <Clock className="mr-2 h-4 w-4" />
            <span>Set as Draft</span>
          </DropdownMenuItem>
        )}
        {status !== "published" && (
          <DropdownMenuItem onClick={() => onStatusChange(listingId, "published")}>
            <CheckCircle className="mr-2 h-4 w-4" />
            <span>Publish</span>
          </DropdownMenuItem>
        )}
        {status !== "archived" && (
          <DropdownMenuItem onClick={() => onStatusChange(listingId, "archived")}>
            <XCircle className="mr-2 h-4 w-4" />
            <span>Archive</span>
          </DropdownMenuItem>
        )}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          className="text-destructive focus:bg-destructive focus:text-destructive-foreground" 
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
